import Cookies from 'js-cookie'
import React, { useState, useEffect } from 'react'
import { SubmitHandler } from 'react-hook-form/dist/index.ie11'
import HttpStatus from 'http-status-codes'
import { GetServerSideProps } from 'next'
import { isNil } from 'lodash'
import { useRouter } from 'next/router'

import { User } from '../../types/models'
import { useTranslation } from '../../i18n'
import { fetchUser } from '../../server/api/user'
import { getHeader, serializePayload, setCookie, isGuestUser } from '../../utils/auth'

import BasicLayout from '../../templates/BasicLayout'
import LoginForm, { LoginFormValues } from '../../components/LoginForm'
import LinkButton from '../../components/LinkButton'
import FacebookLoginButton from '../../components/FacebookLoginButton'
import Alert from '../../components/Alert'
import Separator from '../../components/Separator'
import { hasNextPage, parseNextPageUrl, parseQRVerifyCode } from '../../utils/url'
import { resetKCPSessionStorages } from '../../utils/kcp'

interface Props {
  user: User | null
}

export default function LoginPage({ user }: Props) {
  const { t } = useTranslation()
  const router = useRouter()
  const nextPage = parseNextPageUrl(router.query)
  const qrVerifyCode = parseQRVerifyCode(router.query)

  const [loginError, setLoginError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSnsLoginOnRequest, setIsSnsLoginOnRequest] = useState(false)

  useEffect(() => {
    if (!isNil(user) && !isGuestUser(user)) {
      location.href = nextPage
    }
  }, [])

  useEffect(() => {
    if (qrVerifyCode) {
      setCookie('qrcode_login_verify_code', qrVerifyCode)
    }
  }, [])

  const onSubmit: SubmitHandler<LoginFormValues> = async data => {
    const payload = {
      ...data,
      rememberme: data.isRemember ? 'forever' : '',
      next: nextPage,
    }
    Cookies.remove('sessionid')

    try {
      setIsLoading(true)
      const res = await fetch('/includes15/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: getHeader(),
        body: serializePayload(payload),
      })

      if (!res.ok) {
        throw Error(`${res.status}`)
      }

      // NOTE: 이미 로그인된 사용자의 경우, nextPage로 리다이렉트한다.
      if (res.redirected) {
        location.href = nextPage
      }

      const { result, message: errorMessage, next: nextPath } = await res.json()

      if (result === 'fail') {
        setLoginError(errorMessage)
        setIsLoading(false)
      }
      if (result === 'success') {
        // odk-web에서 kcp 세션 처리를 하기 위해 관련 세션 스토리지를 비운다.
        resetKCPSessionStorages()
        location.href = nextPath
      }
    } catch (error) {
      if (error.message === `${HttpStatus.TOO_MANY_REQUESTS}`) {
        location.href = '/includes15/too-many-try'
      } else {
        setLoginError(t('login.error.server'))
        console.error(error)
      }
      setIsLoading(false)
    }
  }

  return (
    // NOTE: user를 BasicLayout에서 조회하게 하고 여기선 fowardRef로 받는 게 나을까?
    <BasicLayout user={user}>
      <div className="Login">
        <h1 className="Login__title">{t('login.title')}</h1>
        {loginError && <Alert className="Login__alert" text={loginError} />}
        <LoginForm isSnsLoginOnRequest={isSnsLoginOnRequest} isLoading={isLoading} onSubmit={onSubmit} />
        <div className="Login__signup">
          {t('login.notmember')} &nbsp;
          <LinkButton
            title={t('login.signup')}
            theme="text"
            size="large"
            color="green"
            testId="signupLink"
            to={`/includes15/signup${hasNextPage(nextPage) ? `?next=${nextPage}` : ''}`}
          />
        </div>
        <Separator className="Login__separator" />
        <FacebookLoginButton
          onRequest={state => setIsSnsLoginOnRequest(state)}
          onError={errorMessage => setLoginError(errorMessage)}
          disabled={isLoading || isSnsLoginOnRequest}
        />
        {/* <AppleLoginButton /> */}
      </div>
    </BasicLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ req, query }) => {
  const nextPage = parseNextPageUrl(query)
  const user = await fetchUser(req)

  if (!isGuestUser(user)) {
    return {
      redirect: {
        destination: nextPage,
        permanent: false,
      },
    }
  }
  return {
    props: {
      user,
    },
  }
}
